/**
 * RackPortsTab — Interfaces & Power Ports tab in the left inspector.
 * Interactive: click port to toggle status (Free / Connected / Reserved).
 * SRP: displays port matrix for the selected device with Trace and status actions.
 */
import { useState, useRef, useEffect } from 'react';
import { Zap, Wifi, Shield, Circle, ChevronDown } from 'lucide-react';
import { api } from '../../utils/api';

interface Port {
  id: string | number;
  name: string;
  port_type?: string;
  status?: string;
  connected_cable_id?: string | null;
  [key: string]: any;
}

interface RackPortsTabProps {
  selectedEq: { id: string | number; name: string } | null;
  ports: Port[];
  workspace: string;
  onTrace: (portId: string | number) => void;
  onPortStatusChange?: () => void;
  highlightPortId?: string | number | null;
}

// --- Helpers ---

const POWER_TYPES = new Set(['C13', 'C14', 'C19', 'C20', 'NEMA-5-15', 'power']);
const MGMT_TYPES  = new Set(['iDRAC', 'iLO', 'IPMI', 'management', 'RJ11', 'USB-A']);

function getPortCategory(port: Port): 'power' | 'mgmt' | 'data' {
  const t = (port.port_type || '').toLowerCase();
  if (POWER_TYPES.has(port.port_type || '') || t.includes('c13') || t.includes('c19') || t === 'power') return 'power';
  if (MGMT_TYPES.has(port.port_type || '') || t.includes('idrac') || t.includes('ilo') || t.includes('ipmi') || t === 'management') return 'mgmt';
  return 'data';
}

function getPortStatus(port: Port): 'connected' | 'reserved' | 'free' {
  if (port.status === 'Connected' || port.connected_cable_id) return 'connected';
  if (port.status === 'Reserved') return 'reserved';
  return 'free';
}

const STATUS_STYLES = {
  connected: 'border-[rgba(16,185,129,0.35)] bg-[rgba(16,185,129,0.12)] text-[var(--color-success,#10b981)]',
  reserved:  'border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.12)] text-[var(--color-warning,#f59e0b)]',
  free:      'border-[var(--border-color)] bg-[var(--bg-panel)] text-[var(--text-secondary)]',
};

const CATEGORY_ICON = {
  power: <Zap   className="w-3 h-3 text-[var(--color-warning,#f59e0b)]" />,
  mgmt:  <Shield className="w-3 h-3 text-[var(--accent)]" />,
  data:  <Wifi  className="w-3 h-3 text-[var(--color-accent-blue,#38bdf8)]" />,
};

// --- Port status overlay ---

interface PortOverlayProps {
  port: Port;
  workspace: string;
  onClose: () => void;
  onSaved: () => void;
  onTrace: (portId: string | number) => void;
}

function PortOverlay({ port, workspace, onClose, onSaved, onTrace }: PortOverlayProps) {
  const [status, setStatus] = useState<string>(port.status || 'Free');
  const [note, setNote] = useState<string>(port.notes || '');
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const save = async (newStatus?: string) => {
    const finalStatus = newStatus || status;
    setSaving(true);
    try {
      await api.patch(`/api/equipment/ports/${port.id}`, { status: finalStatus, notes: note }, { params: { workspace } });
      onSaved();
      onClose();
    } catch (e) {
      console.error('Port update failed', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={ref}
      className="absolute z-50 top-full left-0 mt-1 w-56 rounded-sm border p-3 text-xs space-y-2 shadow-2xl"
      style={{
        background: 'var(--bg-panel)',
        borderColor: 'var(--border-color)',
        color: 'var(--text-primary)',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div className="font-bold flex justify-between items-center" style={{ color: 'var(--text-primary)' }}>
        <span>{port.name}</span>
        <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{port.port_type}</span>
      </div>

      <div className="space-y-1">
        {port.connected_cable_id ? (
          <div
            className="flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs border"
            style={{
              background: 'rgba(16, 185, 129, 0.12)',
              borderColor: 'rgba(16, 185, 129, 0.4)',
              color: 'var(--color-success, #10b981)',
            }}
          >
            <span>🔗</span>
            <span>Подключён (кабель #{String(port.connected_cable_id).slice(0, 8)})</span>
          </div>
        ) : (
          <select
            value={status}
            onChange={e => {
              setStatus(e.target.value);
              save(e.target.value);
            }}
            className="w-full rounded-sm px-2 py-1.5 text-xs focus:outline-none cursor-pointer"
            style={{
              background: 'var(--bg-input)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="Free">Свободен</option>
            <option value="Reserved">Зарезервирован</option>
          </select>
        )}
      </div>

      {/* Note — context for Reserved or manual Connected */}
      {(status === 'Connected' || status === 'Reserved') && (
        <div className="space-y-1">
          <input
            type="text"
            placeholder={status === 'Connected' ? 'Куда подключён: устройство / порт' : 'Причина резерва...'}
            value={note}
            onChange={e => setNote(e.target.value)}
            className="w-full rounded-sm px-2 py-1 focus:outline-none"
            style={{
              background: 'var(--bg-input)',
              border: status === 'Connected' ? '1px solid rgba(16,185,129,0.5)' : '1px solid rgba(245,158,11,0.5)',
              color: 'var(--text-primary)',
            }}
          />
          {status === 'Connected' && (
            <p className="text-[10px]" style={{ color: 'var(--color-success, #10b981)' }}>
              ⚡ Трассировка недоступна. Добавьте кабель в «Коммутации» для полной интеграции.
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => save()}
          disabled={saving || !!port.connected_cable_id}
          className="flex-1 rounded-sm py-1 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed text-[11px]"
          style={{
            background: 'var(--accent)',
            color: 'var(--text-on-accent, #ffffff)',
          }}
        >
          {saving ? '...' : 'Сохранить'}
        </button>
        <button
          onClick={() => { onTrace(port.id); onClose(); }}
          className="px-2 rounded-sm transition font-bold text-[11px]"
          style={{
            background: 'var(--bg-active)',
            color: 'var(--accent)',
            border: '1px solid var(--border-focus)',
          }}
          title="Trace"
        >
          ↗
        </button>
      </div>
    </div>
  );
}

// --- Main component ---

export function RackPortsTab({
  selectedEq,
  ports,
  workspace,
  onTrace,
  onPortStatusChange,
  highlightPortId,
}: RackPortsTabProps) {
  const [openPortId, setOpenPortId] = useState<string | number | null>(null);
  // For focus restoration after overlay closes (Fix #14)
  const triggerRefs = useRef<Record<string | number, HTMLButtonElement | null>>({});

  if (!selectedEq) return null;

  const grouped = {
    data:  ports.filter(p => getPortCategory(p) === 'data'),
    mgmt:  ports.filter(p => getPortCategory(p) === 'mgmt'),
    power: ports.filter(p => getPortCategory(p) === 'power'),
  };

  const usedCount = ports.filter(p => p.status === 'Connected' || p.connected_cable_id).length;
  const freeCount = ports.length - usedCount;

  return (
    <div className="space-y-3">
      {/* Device summary pill */}
      <div
        className="flex items-center gap-2 p-2 rounded-sm text-xs border"
        style={{
          background: 'var(--bg-app)',
          borderColor: 'var(--border-color)',
        }}
      >
        <span className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
          {selectedEq.name}
        </span>
        <span style={{ color: 'var(--text-muted)' }}>·</span>
        <span className="font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>
          #{selectedEq.id}
        </span>
      </div>

      <div
        className="border p-3 rounded-sm"
        style={{
          background: 'var(--bg-app)',
          borderColor: 'var(--border-color)',
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-bold uppercase text-[10px] tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Порты ({ports.length})
          </h3>
          <div className="flex gap-2 text-[10px] font-mono tabular-nums">
            <span style={{ color: 'var(--color-success, #10b981)' }}>{usedCount} занято</span>
            <span style={{ color: 'var(--text-muted)' }}>{freeCount} свободно</span>
          </div>
        </div>

        {/* Port groups */}
        {(['data', 'mgmt', 'power'] as const).map(cat => {
          const group = grouped[cat];
          if (group.length === 0) return null;
          const label = cat === 'data' ? 'Data' : cat === 'mgmt' ? 'Management' : 'Power';
          return (
            <div key={cat} className="mb-3">
              <div
                className="text-[9px] uppercase mb-1 flex items-center gap-1 font-semibold tracking-wider"
                style={{ color: 'var(--text-muted)' }}
              >
                {CATEGORY_ICON[cat]} {label}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {group.map(port => {
                  const portStatus = getPortStatus(port);
                  const isOpen = String(openPortId) === String(port.id);
                  const isHighlighted =
                    highlightPortId != null &&
                    String(highlightPortId).trim() === String(port.id).trim();

                  return (
                    <div key={port.id} className="relative">
                      <button
                        ref={el => { triggerRefs.current[port.id] = el; }}
                        onClick={() => setOpenPortId(isOpen ? null : port.id)}
                        aria-expanded={isOpen}
                        aria-haspopup="dialog"
                        className={`w-full p-2 rounded-sm border flex flex-col gap-0.5 transition hover:brightness-110 text-left ${
                          isHighlighted
                            ? 'ring-2 ring-[var(--accent)] border-[var(--accent)] bg-[var(--bg-active)]'
                            : STATUS_STYLES[portStatus]
                        }`}
                      >
                        {/* Status dot */}
                        <div className="flex justify-between items-center gap-1">
                          <span className="font-bold text-[10px] min-w-0 flex-1 truncate">{port.name}</span>
                          {isHighlighted ? (
                            <span
                              className="text-[9px] px-1 rounded-sm font-black"
                              style={{ background: 'var(--accent)', color: 'var(--text-on-accent, #ffffff)' }}
                            >
                              ★
                            </span>
                          ) : (
                            <Circle className={`w-2 h-2 flex-shrink-0 ${
                              portStatus === 'connected' ? 'fill-emerald-400 text-emerald-400' :
                              portStatus === 'reserved'  ? 'fill-amber-400  text-amber-400'  :
                              'fill-[var(--text-muted)] text-[var(--text-muted)]'
                            }`} />
                          )}
                        </div>
                        <div className="flex items-center gap-0.5">
                          <span className="text-[8px] uppercase opacity-60 truncate">{port.port_type || 'RJ45'}</span>
                          <ChevronDown className="w-2 h-2 opacity-40 flex-shrink-0" />
                        </div>
                      </button>

                      {isOpen && (
                        <PortOverlay
                          port={port}
                          workspace={workspace}
                          onClose={() => {
                            setOpenPortId(null);
                            // Restore focus to trigger button (Fix #14)
                            triggerRefs.current[port.id]?.focus();
                          }}
                          onSaved={() => { onPortStatusChange?.(); setOpenPortId(null); }}
                          onTrace={onTrace}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {ports.length === 0 && (
          <div className="text-center py-6 text-xs" style={{ color: 'var(--text-muted)' }}>
            Нет портов у выбранного устройства.
          </div>
        )}
      </div>
    </div>
  );
}
